import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { api, DfeLegacyImportResult, DfePositionConflict } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ImportResultDisplayProps {
  result: DfeLegacyImportResult;
  onReset: () => void;
  onRetry?: () => void;
}

export function ImportResultDisplay({ result, onReset, onRetry }: ImportResultDisplayProps) {
  const isSuccess = result.importStatus === 'completed';
  const hasSkipped = result.skippedItems.length > 0;
  const [positionConflicts, setPositionConflicts] = useState<DfePositionConflict[]>([]);

  // Check for position conflicts after a successful import — only DFE has placement grids,
  // so LitSquad doesn't participate here.
  useEffect(() => {
    if (!isSuccess || result.dfeItemsImported === 0) return;
    api
      .getDfePositionConflicts()
      .then((data) => setPositionConflicts(data.conflicts))
      .catch(() => {
        /* ignore — non-critical */
      });
  }, [isSuccess, result.dfeItemsImported]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Import {isSuccess ? 'Complete' : 'Failed'}
          <Badge variant={isSuccess ? 'default' : 'destructive'}>{result.importStatus}</Badge>
        </CardTitle>
        <CardDescription>
          Legacy account: {result.legacyUsername} ({result.legacyServer})
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isSuccess && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-muted rounded-lg">
                <div className="text-2xl font-bold">{result.itemsImported}</div>
                <div className="text-sm text-muted-foreground">Total Imported</div>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <div className="text-2xl font-bold">{result.itemsSkipped}</div>
                <div className="text-sm text-muted-foreground">Total Skipped</div>
              </div>
            </div>

            <SourceBreakdown
              title="DFE Items"
              imported={result.dfeItemsImported}
              skipped={result.dfeItemsSkipped}
              skippedItems={result.dfeSkippedItems}
              inventoryLink="/a/arche_dfe"
              inventoryLabel="View DFE Inventory"
            />

            <SourceBreakdown
              title="LitSquad NFTs"
              imported={result.litsquadItemsImported}
              skipped={result.litsquadItemsSkipped}
              skippedItems={result.litsquadSkippedItems}
              inventoryLink="/a/arche_litsquad"
              inventoryLabel="View LitSquad"
              skippedHint="Asset names that didn't start with 'LitSquad <species>' are skipped — add the species to the list in packages/db-types/src/litsquad.ts."
            />
          </div>
        )}

        {result.errorMessage && (
          <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
            {result.errorMessage}
          </div>
        )}

        {positionConflicts.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm">
            <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
            <div>
              <span className="font-medium">DFE position conflicts detected: </span>
              {positionConflicts.map((c, i) => (
                <span key={`${c.category}:${c.position}`}>
                  {i > 0 && ', '}
                  {c.category}/{c.position}
                </span>
              ))}
              {'. '}Check your inventory for details.
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          {isSuccess && hasSkipped && onRetry && (
            <Button variant="secondary" onClick={onRetry}>
              Retry Skipped Items
            </Button>
          )}
          <Button variant="outline" onClick={onReset}>
            {isSuccess ? 'Import Another' : 'Try Again'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface SourceBreakdownProps {
  title: string;
  imported: number;
  skipped: number;
  skippedItems: string[];
  inventoryLink: string;
  inventoryLabel: string;
  skippedHint?: string;
}

function SourceBreakdown({
  title,
  imported,
  skipped,
  skippedItems,
  inventoryLink,
  inventoryLabel,
  skippedHint,
}: SourceBreakdownProps) {
  if (imported === 0 && skipped === 0) return null;
  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{title}</span>
        <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs">
          <Link to={inventoryLink}>{inventoryLabel}</Link>
        </Button>
      </div>
      <div className="flex gap-4 text-sm">
        <span>
          <span className="font-medium">{imported}</span> imported
        </span>
        <span className="text-muted-foreground">
          <span className="font-medium">{skipped}</span> skipped
        </span>
      </div>
      {skippedItems.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs text-muted-foreground">
            Show skipped items ({skippedItems.length})
          </summary>
          <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
            {skippedItems.map((name, i) => (
              <div
                key={`${name}-${i}`}
                className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded"
              >
                {name}
              </div>
            ))}
          </div>
          {skippedHint && (
            <p className="text-xs text-muted-foreground mt-2 italic">{skippedHint}</p>
          )}
        </details>
      )}
    </div>
  );
}
